import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { PartyPopper, Copy, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';

const MILESTONES = {
  submitted: { title: 'Application submitted!', line: 'I just applied for', accent: '#7C3AED' },
  under_review: { title: 'Under review — the hard part is done', line: 'My application is under review for', accent: '#2563EB' },
  approved: { title: 'Approved! This is a big deal.', line: 'I was approved for', accent: '#16A34A' },
};

export const isMilestone = (prev, next) => ['submitted', 'under_review', 'approved'].includes(next) && prev !== next;

export const MilestoneCard = ({ app, onClose }) => {
  const canvasRef = useRef();
  const m = MILESTONES[app?.status];

  useEffect(() => {
    if (!app) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ['#0F6E6E', '#C26227', '#1E5888', '#E6F4EA'], scalar: 0.9 });
  }, [app]);

  if (!app || !m) return null;
  const text = `${m.line} ${app.program_name} using AccessBenefits Ontario. One step closer. 💪 ${window.location.origin}`;

  const copy = () => { navigator.clipboard?.writeText(text); toast.success('Copied — paste it anywhere'); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: m.title, text }); } catch {} }
    else copy();
  };
  const download = () => {
    const c = document.createElement('canvas'); c.width = 1080; c.height = 1080;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FAF7F2'; ctx.fillRect(0, 0, 1080, 1080);
    ctx.fillStyle = m.accent; ctx.fillRect(0, 0, 1080, 28);
    ctx.fillStyle = '#0F6E6E'; ctx.font = 'bold 44px Helvetica, Arial'; ctx.fillText('AccessBenefits Ontario', 80, 140);
    ctx.fillStyle = '#1C1917'; ctx.font = 'bold 72px Helvetica, Arial'; ctx.fillText(m.title, 80, 300);
    ctx.font = '48px Helvetica, Arial'; ctx.fillStyle = '#44403C'; ctx.fillText(m.line, 80, 420);
    ctx.font = 'bold 56px Helvetica, Arial'; ctx.fillStyle = m.accent;
    const words = app.program_name.split(' '); let line = ''; let y = 510;
    words.forEach((w) => { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > 920) { ctx.fillText(line, 80, y); y += 70; line = w; } else line = t; });
    ctx.fillText(line, 80, y);
    ctx.font = '36px Helvetica, Arial'; ctx.fillStyle = '#78716C'; ctx.fillText(new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }), 80, 940);
    ctx.fillText('One step closer.', 80, 1000);
    const a = document.createElement('a'); a.href = c.toDataURL('image/png'); a.download = `i-applied-${app.program_id}.png`; a.click();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-w-md" data-testid="milestone-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Lexend'}}><PartyPopper className="w-5 h-5" style={{ color: m.accent }} /> {m.title}</DialogTitle><DialogDescription>A milestone card you can share. Only the program name is included.</DialogDescription></DialogHeader>
        <div ref={canvasRef} className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: m.accent }} data-testid="milestone-card">
          <div className="text-xs uppercase tracking-wider opacity-80">AccessBenefits Ontario</div>
          <div className="mt-4 text-sm opacity-90">{m.line}</div>
          <div className="text-2xl font-bold leading-tight mt-1" style={{fontFamily: 'Lexend'}}>{app.program_name}</div>
          <div className="mt-5 text-xs opacity-80">{new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })} · One step closer.</div>
        </div>
        <p className="text-sm text-muted-foreground">Every application is progress worth marking.</p>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={share} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="milestone-share"><Share2 className="w-4 h-4 mr-2" /> Share</Button>
          <Button variant="outline" onClick={copy} data-testid="milestone-copy"><Copy className="w-4 h-4 mr-2" /> Copy text</Button>
          <Button variant="outline" onClick={download} data-testid="milestone-download"><Download className="w-4 h-4 mr-2" /> Save image</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
